import React, { useState } from 'react';
import { X, ChevronRight, ChevronLeft, BarChart3, Bot, MessageCircle, Trophy, Filter, Search } from 'lucide-react';

const TutorialModal = ({ isOpen, onClose }) => {
  const [currentStep, setCurrentStep] = useState(0);

  const tutorialSteps = [
    {
      title: "Welcome to Clairity! 🎉",
      icon: <Bot size={24} />,
      content: (
        <div>
          <p className="tutorial-intro">
            Clairity helps you analyze and understand your Camelback Resort guest reviews with powerful AI insights.
          </p>
          <div className="feature-highlight">
            <div className="highlight-item">
              <BarChart3 size={16} />
              <span>Interactive dashboards</span>
            </div>
            <div className="highlight-item">
              <Bot size={16} />
              <span>AI-powered analysis</span>
            </div>
            <div className="highlight-item">
              <MessageCircle size={16} />
              <span>Natural language chat</span>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Dashboard Overview",
      icon: <BarChart3 size={24} />,
      content: (
        <div>
          <p>Your main dashboard shows:</p>
          <ul className="tutorial-list">
            <li><strong>Summary Statistics</strong> - Overall review counts and ratings</li>
            <li><strong>Business Segment Breakdown</strong> - Performance by area (Hotel, Ski, Aquatopia, etc.)</li>
            <li><strong>Filter Controls</strong> - Narrow down reviews by segment, rating, date, or search text</li>
            <li><strong>Paginated Review Table</strong> - Browse through reviews 50 at a time</li>
          </ul>
          <div className="tutorial-tip">
            💡 <strong>Tip:</strong> Use the new preset date filters (Last 30 Days, 3 Months, etc.) for quick analysis!
          </div>
        </div>
      )
    },
    {
      title: "Smart Filtering",
      icon: <Filter size={24} />,
      content: (
        <div>
          <p>Filter your data to focus on what matters:</p>
          <ul className="tutorial-list">
            <li><strong>Business Segment</strong> - Analyze specific areas like Aquatopia or Ski Mountain</li>
            <li><strong>Rating Filter</strong> - Focus on 5-star experiences or 1-star issues</li>
            <li><strong>Date Range</strong> - Choose presets (Last 30 Days, 6 Months) or custom dates</li>
            <li><strong>Text Search</strong> - Find reviews mentioning specific topics</li>
          </ul>
          <div className="tutorial-tip">
            💡 <strong>Pro Tip:</strong> Filters apply across all tabs - set them once and explore different views!
          </div>
        </div>
      )
    },
    {
      title: "AI Chat Assistant",
      icon: <MessageCircle size={24} />,
      content: (
        <div>
          <p>Chat with Clairity to get instant insights:</p>
          <ul className="tutorial-list">
            <li><strong>Ask Questions</strong> - "What do guests say about wait times?"</li>
            <li><strong>Get Specific Data</strong> - "Show me 1-star reviews from December"</li>
            <li><strong>Find Trends</strong> - "How do ratings compare between segments?"</li>
            <li><strong>Use Suggestions</strong> - Click the pill-shaped prompt suggestions to get started</li>
          </ul>
          <div className="tutorial-tip">
            💡 <strong>New:</strong> Responses now include review counts and are beautifully formatted!
          </div>
        </div>
      )
    },
    {
      title: "AI Insights & Analysis",
      icon: <Bot size={24} />,
      content: (
        <div>
          <p>Discover deeper insights with our AI tools:</p>
          <ul className="tutorial-list">
            <li><strong>AI Insights</strong> - Sentiment analysis, themes, and recommendations</li>
            <li><strong>AI Summary</strong> - Executive summaries of your review data</li>
            <li><strong>Trend Analysis</strong> - Rating patterns and seasonal trends</li>
            <li><strong>Peak Performers</strong> - Staff members mentioned positively in reviews</li>
          </ul>
          <div className="tutorial-tip">
            💡 <strong>Note:</strong> AI features require manual activation for better performance - click "Generate" buttons when ready!
          </div>
        </div>
      )
    },
    {
      title: "Peak Performers",
      icon: <Trophy size={24} />,
      content: (
        <div>
          <p>Recognize your star team members:</p>
          <ul className="tutorial-list">
            <li><strong>AI-Powered Recognition</strong> - Automatically finds staff mentioned in positive reviews</li>
            <li><strong>Review Carousel</strong> - Browse through multiple mentions for each employee</li>
            <li><strong>Segment Filtering</strong> - See top performers by business area</li>
            <li><strong>Time Filters</strong> - Analyze performance over different periods</li>
          </ul>
          <div className="tutorial-tip">
            💡 <strong>Feature:</strong> Names are intelligently matched (Mike/Michael/Mick are recognized as the same person)!
          </div>
        </div>
      )
    },
    {
      title: "Ready to Get Started! 🚀",
      icon: <Search size={24} />,
      content: (
        <div>
          <p className="tutorial-intro">
            You're all set to explore your guest feedback with Clairity!
          </p>
          <div className="getting-started">
            <h4>Quick Start Suggestions:</h4>
            <ul className="tutorial-list">
              <li>Start with the <strong>Dashboard</strong> to see your overall performance</li>
              <li>Try filtering by <strong>last 30 days</strong> to see recent trends</li>
              <li>Visit <strong>AI Chat</strong> and click a suggestion to ask your first question</li>
              <li>Check out <strong>Peak Performers</strong> to celebrate your team</li>
            </ul>
          </div>
          <div className="tutorial-tip">
            💡 Remember: The navigation bar stays fixed as you scroll, so you can always switch between sections!
          </div>
        </div>
      )
    }
  ];

  const nextStep = () => {
    if (currentStep < tutorialSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleClose = () => {
    setCurrentStep(0);
    onClose();
  };

  if (!isOpen) return null;

  const currentTutorial = tutorialSteps[currentStep];

  return (
    <div className="tutorial-overlay">
      <div className="tutorial-modal">
        <div className="tutorial-header">
          <div className="tutorial-title">
            <div className="tutorial-icon">
              {currentTutorial.icon}
            </div>
            <h2>{currentTutorial.title}</h2>
          </div>
          <button className="tutorial-close" onClick={handleClose}>
            <X size={20} />
          </button>
        </div>

        <div className="tutorial-content">
          {currentTutorial.content}
        </div>

        <div className="tutorial-footer">
          <div className="tutorial-progress">
            <span className="step-counter">
              {currentStep + 1} of {tutorialSteps.length}
            </span>
            <div className="progress-dots">
              {tutorialSteps.map((_, index) => (
                <div
                  key={index}
                  className={`progress-dot ${index === currentStep ? 'active' : ''} ${index < currentStep ? 'completed' : ''}`}
                />
              ))}
            </div>
          </div>

          <div className="tutorial-buttons">
            {currentStep > 0 && (
              <button className="tutorial-btn secondary" onClick={prevStep}>
                <ChevronLeft size={16} />
                Previous
              </button>
            )}
            
            {currentStep < tutorialSteps.length - 1 ? (
              <button className="tutorial-btn primary" onClick={nextStep}>
                Next
                <ChevronRight size={16} />
              </button>
            ) : (
              <button className="tutorial-btn primary" onClick={handleClose}>
                Get Started!
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TutorialModal;